import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router, withInMemoryScrolling } from '@angular/router';
import localeUk from '@angular/common/locales/uk';
import localeEn from '@angular/common/locales/en';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NgxsModule } from '@ngxs/store';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgxMaskConfig, provideEnvironmentNgxMask } from 'ngx-mask';
import { AuthInterceptor } from '@core/interceptors/auth-interceptor';
import { LoaderInterceptor } from '@core/interceptors/loader-interceptor';
import * as Sentry from '@sentry/angular';
import { environment } from '@environments/environment';
import { ZonesState } from '@core/store/state/zone.state';
import { VehiclesState } from '@core/store/state/vehicle.state';
import { ReferencesState } from '@core/store/state/reference.state';
import { TimeWindowState } from '@core/store/state/time-window.state';
import { PharmaciesState } from '@core/store/state/pharmacy.state';
import { TranslateHttpLoader, TRANSLATE_HTTP_LOADER_CONFIG } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { LocaleService } from '@core/services/locale.service';
import { registerLocaleData } from '@angular/common';
import { ErrorInterceptor } from '@core/interceptors/error-interceptor';
import { GraphqlStatusInterceptor } from '@core/interceptors/graphql-status-interceptor';
import { RequestTimeWindowState } from '@core/store/state/requests-time-windows.state';
import { CourierSchedulesState } from '@core/store/state/courier-work-schedule.state';
import { CouriersState } from '@core/store/state/courier.state';
import { AdminDataState } from '@core/store/state/admin-data.state';
import { OrderReferenceGuideState } from '@core/store/state/order-reference-guide.state';
import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY } from '@core/constants/admin.constants';
import { UserRightsState } from '@core/store/state/user-rights-state';
import { NomenclatureBalancesState } from '@core/store/state/nomenclature-balances.state';
import { UserRolesState } from '@core/store/state/user-roles.state';
import { RouteSheetsState } from '@core/store/state/route-sheets.state';
import { UsersState } from '@core/store/state/users.state';
import { WaypointSettingsState } from '@core/store/state/waypoint-settings.state';
import { RouteSheetsTransitState } from '@core/store/state/route-sheets-transit.state';
import { ChangePerformerState } from '@core/store/state/change-performer.state';
import { ParcelMachineState } from '@core/store/state/parcel-machine.state';
import { InstructionState } from '@core/store/state/instructions.state';
import { DashboardState } from '@core/store/state/dashboard.state';
import { CourierTaskState } from '@core/store/state/courier-task.state';
import { CourierBalancesState } from '@core/store/state/courier-balance.state';
import { SalaryCalculationState } from '@core/store/state/salary-calculation.state';
import { StaleOrderState } from '@core/store/state/stale-orders.state';
import { TransferOrderState } from '@core/store/state/transfer-order.state';
import { RouteHistoryState } from '@core/store/state/route-history.state';
import { WarehousesState } from '@core/store/state/warehouse.state';
import { PostponedOrdersState } from '@core/store/state/postponed-orders.state';
import { CorporateCardState } from '@core/store/state/corporate-card.state';
import { PharmacyCuratorState } from '@core/store/state/pharmacy-curator.state';
import { routes } from './app.routes';
import { graphqlProvider } from './graphql.provider';

const maskConfig: Partial<NgxMaskConfig> = {
  validation: false,
};

export function initializeApp(translateService: TranslateService): () => void {
  return () => {
    const locale = localStorage.getItem(LOCALE_STORAGE_KEY) || DEFAULT_LOCALE;
    return firstValueFrom(translateService.use(locale!));
  };
}

registerLocaleData(localeUk, 'uk');
registerLocaleData(localeEn, 'en');

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
    ),
    provideAnimationsAsync('animations'),
    provideEnvironmentNgxMask(maskConfig),
    provideHttpClient(),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: `${environment.serverUrl}public/dms/translations/`,
        suffix: '?updatedAfter=2024-12-20',
      },
    },

    importProvidersFrom(
      NgxsModule.forRoot(
        [
          ZonesState,
          CouriersState,
          PharmaciesState,
          TimeWindowState,
          OrderReferenceGuideState,
          VehiclesState,
          AdminDataState,
          ReferencesState,
          CourierSchedulesState,
          RequestTimeWindowState,
          UserRightsState,
          NomenclatureBalancesState,
          UserRolesState,
          RouteSheetsState,
          UsersState,
          WaypointSettingsState,
          RouteSheetsTransitState,
          ChangePerformerState,
          ParcelMachineState,
          InstructionState,
          DashboardState,
          CourierTaskState,
          CourierBalancesState,
          SalaryCalculationState,
          StaleOrderState,
          TransferOrderState,
          RouteHistoryState,
          WarehousesState,
          PostponedOrdersState,
          CorporateCardState,
          PharmacyCuratorState,
        ],
        {
          developmentMode: !environment.production,
        },
      ),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: TranslateHttpLoader,
        },
      }),
    ),
    graphqlProvider,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GraphqlStatusInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [TranslateService, LocaleService],
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'uk' },
  ],
};
